import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from './LoadingSpinner';

// Guard for authenticated routes. It must wait for the session check to finish
// before deciding: on a hard reload the auth context starts with `loading = true`
// and no user yet, so checking `isAuthenticated` too early redirects to /login —
// and /login then bounces the (actually authenticated) user to /me, silently
// dumping them at the dashboard and losing their place. Rendering a spinner during
// `loading` keeps them on the route until auth resolves.
// `isAdmin` requires the admin role; `isEditor` admits editor OR admin (Content desk).
const ProtectedRoute = ({ children, isAdmin = false, isEditor = false }) => {
  const { isAuthenticated, isAdmin: userIsAdmin, isEditor: userIsEditor, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner />;
  }
  if (!isAuthenticated) {
    // Carry the page the user asked for through login (the token is per-tab
    // sessionStorage, so a deep link in a fresh tab always lands here first).
    // LoginPage honours a same-origin ?next=; without it every deep link would
    // dump the user at the post-login default.
    const next = `${location.pathname}${location.search || ''}`;
    const dest = next && next !== '/' ? `/login?next=${encodeURIComponent(next)}` : '/login';
    return <Navigate to={dest} replace />;
  }
  if (isAdmin && !userIsAdmin) {
    return <Navigate to="/" replace />;
  }
  if (isEditor && !(userIsEditor || userIsAdmin)) {
    return <Navigate to="/" replace />;
  }
  return children;
};

export default ProtectedRoute;
